import { useQuery } from '@apollo/client/react'
import { Link } from '@faceup/router'
import { Card, ContentLayout } from '@faceup/ui'
import {
  Alert,
  Col,
  Input,
  MultiSelect,
  Row,
  Space,
  Table,
  type TableColumns,
  Tag,
} from '@faceup/ui-base'
import {
  ChannelType,
  ChargebeeSubscriptionStatus,
  convertRegionToRegionName,
  PhoneNumberStatus,
  type Region,
  regionToRegionNameMap,
  SupportedRegion,
} from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useMemo, useState } from 'react'
import { graphql } from '../__generated__'
import { InputLabel } from '../Components/InputLabel'

const query = graphql(`
  query ExternalInternalHotlinesQuery {
    externalInternalHotlines {
      unavailableRegions
      items {
        id
        regionId
        phoneDiscoveryRegionId
        phone
        name
        hotlineType
        isEnabled
        status
        institution {
          id
          name
          subscriptionStatus
        }
      }
    }
  }
`)

type Hotline = NonNullable<ResultOf<typeof query>['externalInternalHotlines']>['items'][number]

const statusMessages: Record<PhoneNumberStatus, string> = {
  [PhoneNumberStatus.Everywhere]: '✅',
  [PhoneNumberStatus.DatabaseOnly]: 'Phone number is in a database only - Hotline will not work',
  [PhoneNumberStatus.MissingFromPhoneDiscovery]:
    'Phone number is missing from Phone Discovery - Hotline will not work',
  [PhoneNumberStatus.MissingFromVonage]:
    'Phone number is missing from Vonage - Hotline will not work',
  [PhoneNumberStatus.VonageOnly]:
    'Phone number is bought only in Vonage - We are paying without use',
  [PhoneNumberStatus.PhoneDiscoveryOnly]:
    'Phone number is only in Phone Discovery - Hotline will not work',
  [PhoneNumberStatus.MissingFromDatabase]:
    'Phone number is missing from the database of every region - We are paying without use',
  [PhoneNumberStatus.Nowhere]: 'Phone number not found anywhere',
}

const hotlineTypeMessages: Record<ChannelType, string> = {
  [ChannelType.AutomatedHotline]: 'Automated',
  [ChannelType.AiHotline]: 'AI',
  [ChannelType.LiveHotline]: 'Live',
  [ChannelType.ReportingSystem]: 'Reporting system',
  [ChannelType.Survey]: 'Survey',
}

const internalPhoneNumbers = ['+15074322387', '+420234674152', '+420225636014', '+420296216767']

const formatRegionName = (region: string) => regionToRegionNameMap[region as Region] ?? region

const formatRegionNames = (regions: readonly string[]) => regions.map(formatRegionName).join(', ')

export const VonageHotlinesView = () => {
  const [statusFilter, setStatusFilter] = useState<PhoneNumberStatus[]>([])
  const [billingStatusFilter, setBillingStatusFilter] = useState<ChargebeeSubscriptionStatus[]>([])
  const [phoneFilter, setPhoneFilter] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [regionFilter, setRegionFilter] = useState<SupportedRegion[]>([])

  const { data, loading } = useQuery(query)

  const hotlines = data?.externalInternalHotlines?.items ?? []
  const unavailableRegions = data?.externalInternalHotlines?.unavailableRegions ?? []

  const filteredHotlines = useMemo(
    () =>
      hotlines.filter(({ status, phone, institution, regionId, phoneDiscoveryRegionId }) => {
        if (statusFilter.length > 0 && !statusFilter.includes(status)) {
          return false
        }

        if (
          billingStatusFilter.length > 0 &&
          (!institution?.subscriptionStatus ||
            !billingStatusFilter.includes(institution.subscriptionStatus))
        ) {
          return false
        }

        if (phoneFilter && !phone.toLowerCase().includes(phoneFilter.toLowerCase())) {
          return false
        }

        if (
          institutionFilter &&
          !institution?.name.toLowerCase().includes(institutionFilter.toLowerCase())
        ) {
          return false
        }

        // A number can be routed to one region while its hotline lives in another.
        if (
          regionFilter.length > 0 &&
          !regionFilter.some(region => region === regionId || region === phoneDiscoveryRegionId)
        ) {
          return false
        }

        return true
      }),
    [hotlines, statusFilter, billingStatusFilter, phoneFilter, institutionFilter, regionFilter]
  )

  const columns: TableColumns<Hotline> = [
    {
      title: 'Status',
      key: 'status',
      render: ({ status }) => (
        <span
          className={status !== PhoneNumberStatus.Everywhere ? 'font-bold text-red5' : undefined}
        >
          {statusMessages[status]}
        </span>
      ),
    },
    {
      title: 'Billing status',
      key: 'billingStatus',
      render: ({ institution }) => (
        <span
          className={
            institution?.subscriptionStatus === ChargebeeSubscriptionStatus.Cancelled
              ? 'font-bold text-red5'
              : undefined
          }
        >
          {institution?.subscriptionStatus ?? '-'}
        </span>
      ),
    },
    {
      title: 'Phone',
      key: 'phone',
      render: ({ phone }) =>
        internalPhoneNumbers.includes(phone) ? `${phone} (internal, do not delete)` : phone,
    },
    {
      title: 'Institution',
      key: 'institution',
      render: ({ institution }) =>
        institution ? (
          <Link to={routes => routes.institution({ id: institution.id })}>{institution.name}</Link>
        ) : (
          '-'
        ),
    },
    {
      title: 'Hotline name',
      key: 'name',
      render: ({ name }) => name ?? '-',
    },
    {
      title: 'Hotline type',
      key: 'hotlineType',
      render: ({ hotlineType }) => (hotlineType ? hotlineTypeMessages[hotlineType] : '-'),
    },
    {
      title: 'Hotline enabled',
      key: 'isEnabled',
      render: ({ isEnabled }) =>
        isEnabled === null ? (
          '-'
        ) : (
          <Tag color={isEnabled ? 'green' : 'orange'}>{isEnabled ? 'Yes' : 'No'}</Tag>
        ),
    },
    {
      title: 'Region',
      key: 'regionId',
      render: ({ regionId, phoneDiscoveryRegionId }) => {
        if (!regionId) {
          return '-'
        }

        // Calls are routed to a region that does not have the hotline, so it cannot answer them.
        if (phoneDiscoveryRegionId && phoneDiscoveryRegionId !== regionId) {
          return (
            <span className='font-bold text-red5'>
              {formatRegionName(regionId)} (Phone Discovery routes to{' '}
              {formatRegionName(phoneDiscoveryRegionId)})
            </span>
          )
        }

        return formatRegionName(regionId)
      },
    },
  ]

  return (
    <ContentLayout header={<ContentLayout.Header title='Vonage Hotlines' />}>
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        {unavailableRegions.length > 0 && (
          <Alert
            type='warning'
            message={`Hotlines of ${formatRegionNames(unavailableRegions)} could not be loaded - their numbers are listed as missing from the database.`}
          />
        )}
        <Card>
          <Row gutter={16} style={{ padding: '24px' }}>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Status</InputLabel>
              <MultiSelect
                style={{ width: '100%' }}
                allowClear
                placeholder='All statuses'
                onChange={(value: PhoneNumberStatus[]) => setStatusFilter(value)}
                options={Object.values(PhoneNumberStatus).map(status => ({
                  label: statusMessages[status],
                  value: status,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Billing Status</InputLabel>
              <MultiSelect
                style={{ width: '100%' }}
                allowClear
                placeholder='All billing statuses'
                onChange={(value: ChargebeeSubscriptionStatus[]) => setBillingStatusFilter(value)}
                options={Object.values(ChargebeeSubscriptionStatus).map(status => ({
                  label: status,
                  value: status,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Phone</InputLabel>
              <Input
                placeholder='Search phone'
                onChange={({ target: { value } }) => setPhoneFilter(value)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Institution</InputLabel>
              <Input
                placeholder='Search institution'
                onChange={({ target: { value } }) => setInstitutionFilter(value)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Region</InputLabel>
              <MultiSelect
                style={{ width: '100%' }}
                allowClear
                placeholder='All regions'
                onChange={value => setRegionFilter(value)}
                options={Object.values(SupportedRegion).map(region => ({
                  label: convertRegionToRegionName(region),
                  value: region,
                }))}
              />
            </Col>
          </Row>
        </Card>
        <Table
          shadow
          loading={loading}
          rowKey='id'
          dataSource={filteredHotlines}
          locale={{ emptyText: ' ' }}
          scroll={{ x: 'max-content' }}
          columns={columns}
          pagination={{ pageSize: 1000 }}
        />
      </Space>
    </ContentLayout>
  )
}
